/**
 * Hook for fetching swing trading signals
 *
 * Connects to the swing-signals Cloudflare Worker API.
 */

import { useState, useEffect, useCallback } from 'react';

// Detect if running on localhost (dev mode with Vite proxy)
const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Worker URL - use proxy path in dev, direct URL in production
const WORKER_URL = isLocalDev
  ? '/api/swing-signals'
  : 'https://vectrax-swing-signals.thegpc806.workers.dev';

export default function useSwingSignals(initialTicker = 'TQQQ') {
  const [ticker, setTicker] = useState(initialTicker);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSignals = useCallback(async (tickerToFetch) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${WORKER_URL}?ticker=${tickerToFetch}`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching swing signals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllTickers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${WORKER_URL}?list=true`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error fetching all tickers:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (tickerToFetch, strategy = null) => {
    try {
      const params = new URLSearchParams({ transactions: tickerToFetch });
      if (strategy) params.append('strategy', strategy);

      const response = await fetch(`${WORKER_URL}?${params}`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return null;
    }
  }, []);

  // Fetch signals when ticker changes
  useEffect(() => {
    fetchSignals(ticker);
  }, [ticker, fetchSignals]);

  // Refresh function
  const refresh = useCallback(() => {
    fetchSignals(ticker);
  }, [ticker, fetchSignals]);

  // Change ticker function
  const changeTicker = useCallback((newTicker) => {
    setTicker(newTicker);
  }, []);

  return {
    ticker,
    data,
    loading,
    error,
    lastUpdated,
    refresh,
    changeTicker,
    fetchAllTickers,
    fetchTransactions
  };
}
